import React, { useState } from 'react'
import { useToast } from '~/lib/ui/toast'
import Spinner from '~/components/shared/Spinner'
import { apiPost } from '~/util/api'
import { useDropzone } from 'react-dropzone'
import { cn } from '~/lib/utils'
import { useTranslation } from '~/hooks/useTranslation'

const ChatAttachmentUpload = ({
  className,
  helpText,
  onUploadSuccess,
  ...props
}) => {
  const { userId, chatId } = props
  const { t } = useTranslation('coach_chat')
  const [attachmentIsUploading, setAttachmentIsUploading] = useState(false)
  const [fileName, setFileName] = useState('')
  const [attachmentFieldsArePopulated, setAttachmentFieldsArePopulated] =
    useState(false)
  const [isDraggingOver, setIsDraggingOver] = useState(false)
  const { toast } = useToast()

  const { getRootProps, getInputProps } = useDropzone({
    maxFiles: 1,
    accept: {
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        ['.docx'],
      'text/plain': ['.txt'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
    },
    onDropAccepted: (incomingFiles) => saveNewAttachment(incomingFiles[0]),
    onDropRejected: (incomingFiles) => rejectionMessage(incomingFiles),
    onDragEnter: () => setIsDraggingOver(true),
    onDragLeave: () => setIsDraggingOver(false),
    onDrop: () => setIsDraggingOver(false),
    maxSize: 15_000_000, // 15MB
    disabled: attachmentIsUploading,
  })

  const rejectionMessage = (incomingFiles) => {
    const {
      file: { type },
      errors,
    } = incomingFiles[0]
    const description = errors.map((e) => ` • ${e.message}`).join('\n')
    console.error('File upload rejected:', description)
    if (type) {
      toast({
        description,
        title: t('attachment.could_not_upload_attachment'),
        variant: 'destructive',
        duration: 10_000,
      })
    }
  }

  const saveNewAttachment = async (file) => {
    setAttachmentIsUploading(true)
    const name = file.name.split('.')[0]
    const formData = new FormData()
    formData.append('chat_attachment[remote_content]', file)
    formData.append('chat_attachment[name]', name)
    formData.append('chat_attachment[candidate_id]', userId)
    if (chatId) {
      formData.append('chat_attachment[chat_id]', chatId)
    }

    try {
      const response = await apiPost('/chat_attachments', formData, true)
      setAttachmentIsUploading(false)
      setFileName(name)
      setAttachmentFieldsArePopulated(true)

      // Notify parent component of successful upload
      if (onUploadSuccess) {
        onUploadSuccess(response.data)
      }

      toast({
        description: t('attachment.attachment_uploaded_successfully'),
      })
    } catch (e) {
      setAttachmentIsUploading(false)
      console.error('Error saving new attachment', e)
      console.error('Error details:', {
        message: e.message,
        status: e.status,
        response: e.response,
      })
      toast({
        description: t('attachment.attachment_upload_failed'),
        variant: 'destructive',
      })
      return
    }
  }

  const baseRootClassName = cn(
    'min-h-[20rem] p-4 border-dashed border text-center content-center justify-items-center',
    className,
  )
  const defaultRootClassName = isDraggingOver
    ? baseRootClassName + ' border-primary border-2 bg-lily-blue'
    : baseRootClassName
  const rootClassName = attachmentIsUploading
    ? defaultRootClassName
    : defaultRootClassName + ' cursor-pointer'

  return (
    <div className="px-4 pb-4">
      <div
        {...getRootProps({
          className: rootClassName,
        })}
      >
        <input {...getInputProps()} />
        {attachmentIsUploading && <Spinner show={true} className="w-14" />}
        {!attachmentIsUploading && {
          ...(attachmentFieldsArePopulated ? (
            <div>
              <p>{t('attachment.attachment_uploaded_successfully')}</p>
              <p>{fileName}</p>
            </div>
          ) : (
            <div>
              <p>
                {helpText ||
                  t('attachment.drag_attachment_here_or_click_to_select')}
              </p>
              <em>({t('attachment.required_file_formats')})</em>
            </div>
          )),
        }}
      </div>
    </div>
  )
}

export default ChatAttachmentUpload
